import { useEffect, useState, useRef } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/subscriptionsPage.module.scss';
import { useAuth } from '../context/AuthContext';
import { EnvPaths } from '../helpers/environment';
import axios from 'axios';
import { SubscriptionInterface } from '../helpers/objects/subscriptionObject';
import tableStyles from '../css/table.module.scss';
import { formatStatus } from '../helpers/functions';
import SubscriptionDetailsModal from '../components/subscriptionDetailsModal';
import Pagination from '../components/pagination';
import CustomSearchInput from '../components/customSearchInput';
import { useClickAway } from 'react-use';
import CustomDropdown from '../components/customDropdown';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

const SubscriptionsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<number>(1);
  const [subscriptionsList, setSubscriptionsList] = useState<SubscriptionInterface[]>([]);
  const [isActionOpen, setIsActionOpen] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [subForEdit, setSubForEdit] = useState<SubscriptionInterface | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: 'all', label: t('subscriptions.labels.all') });
  const [filterStatusOpen, setFilterStatusOpen] = useState<boolean>(false);
  const filterStatusDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleActionClick = (id: string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${EnvPaths.SERVER_URL}/admin/get-subscriptions?page=${page}&status=${status.value}&q=${searchTerm}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const { subscriptions, totalPages } = response.data;
      setSubscriptionsList(subscriptions);
      setTotalPages(totalPages);
      return subscriptions;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      if (user) {
        fetchSubscriptions().then((response) => {
          setSubscriptionsList(response ?? []);
          setLoading(false);
        });
      }
    }, 1000);
  }, [page, user, status, searchTerm]);

  useEffect(() => {
    setStatus((prev) => ({ ...prev, label: t('subscriptions.labels.all') }));
  }, [t, i18next]);

  const planDuration = (subscription: SubscriptionInterface) => {
    switch (subscription?.plan?.product?.periodInMonths) {
      case 1:
        return t('subscriptions.planDuration.monthly');
      case 3:
        return t('subscriptions.planDuration.3months');
      case 6:
        return t('subscriptions.planDuration.6months');
      case 12:
        return t('subscriptions.planDuration.12months');
      default:
        return '';
    }
  };

  useClickAway(filterStatusDropRef, () => {
    setFilterStatusOpen(false);
  });

  const filterStatusOptions = [
    { value: 'all', label: t('subscriptions.labels.all') },
    { value: 'active', label: t('subscriptions.labels.active') },
    { value: 'canceled', label: t('subscriptions.labels.canceled') },
  ];

  const handleStatusChange = (item: { value: string; label: string }) => {
    setStatus(item);
    setFilterStatusOpen(false);
  };

  const cancelSubscription = async (id: string) => {
    toast.loading(t('subscriptions.cancelSubscription.loading'));
    try {
      await axios.get(`${EnvPaths.SERVER_URL}/plans/cancel-subscription/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      fetchSubscriptions();
      toast.dismiss();
      toast.success(t('subscriptions.cancelSubscription.success'));
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error(t('subscriptions.cancelSubscription.error'));
    }
  };

  return (
    <>
      {subForEdit && <SubscriptionDetailsModal subscription={subForEdit} isOpen={isOpen} onClose={() => setIsOpen(false)} />}
      <main className={globalPageStyle.pageContainer}>
        <div className={globalPageStyle.pageContent}>
          {/*filters*/}
          <div className={styles.flexRow}>
            <CustomSearchInput onSearch={handleSearch} icon="/assets/images/search-icon.svg" placeholder={t('subscriptions.search')} />
            <CustomDropdown ref={filterStatusDropRef} isOpen={filterStatusOpen} onToggle={() => setFilterStatusOpen(!filterStatusOpen)} options={filterStatusOptions} value={status} onChange={handleStatusChange} width="195px" label={t('subscriptions.status')} />
          </div>
          {/*filters*/}
          <br />
          {user && user?.role === 'admin' ? (
            loading ? (
              <div className="loader" />
            ) : subscriptionsList.length > 0 ? (
              <div className={styles.body}>
                <div className={styles.subPageContainer}>
                  {/*Table*/}
                  <div className={tableStyles.tableContainer}>
                    <table className={`${tableStyles.table}`}>
                      <thead className={tableStyles.tableHeader}>
                        <tr>
                          <th>{t('subscriptions.createdAt')}</th>
                          <th>{t('subscriptions.plan')}</th>
                          <th>{t('subscriptions.status')}</th>
                          <th>{t('subscriptions.userEmail')}</th>
                          <th>{t('subscriptions.skycopCode')}</th>
                          <th>{t('subscriptions.expiringDate')}</th>
                          <th className={tableStyles.actionColumn}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptionsList.map((subscription: SubscriptionInterface) => {
                          const createdAt = subscription?.createdAt
                            ? new Date(subscription.createdAt).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                              })
                            : '';
                          const expiringDate = subscription?.skycopAssignedInfo?.validTo?.date
                            ? new Date(subscription?.skycopAssignedInfo?.validTo?.date).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                              })
                            : '';
                          const plan = `${subscription?.plan.product.family ? t('subscriptions.family') : t('subscriptions.solo')} ${planDuration(subscription)} ` + t('subscriptions.plan');
                          return (
                            <>
                              <tr key={subscription._id}>
                                <td>{createdAt}</td>
                                <td>{plan}</td>
                                <td>{formatStatus(t, subscription?.status)}</td>
                                <td>{subscription?.unregisteredUserEmail}</td>
                                <td>{subscription?.skycopAssignedInfo?.code}</td>
                                <td>{expiringDate}</td>
                                <td className={tableStyles.actionColumn}>
                                  <div className={tableStyles.actionShape} ref={buttonRef}>
                                    <img
                                      src="/assets/images/dots.svg"
                                      alt="dots"
                                      onClick={(e) => {
                                        const buttonElement = e.currentTarget as HTMLDivElement;
                                        handleActionClick(subscription._id, buttonElement);
                                      }}
                                    />
                                    {isActionOpen === subscription._id &&
                                      typeof window !== 'undefined' &&
                                      createPortal(
                                        <div
                                          className={`${tableStyles.actionShapeDropdown}`}
                                          style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`,
                                          }}
                                        >
                                          <div
                                            onClick={() => {
                                              setSubForEdit(subscription);
                                              setIsOpen(true);
                                              setIsActionOpen(null);
                                            }}
                                          >
                                            {t('subscriptions.view')}
                                          </div>

                                          {subscription.status === 'active' && (
                                            <div
                                              onClick={() => {
                                                setIsActionOpen(null);
                                                cancelSubscription(subscription.subscriptionId);
                                              }}
                                            >
                                              {t('subscriptions.cancelSubscriptions')}
                                            </div>
                                          )}
                                        </div>,
                                        document.body
                                      )}
                                  </div>
                                </td>
                              </tr>
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/*Table*/}
                  {/*Pagination*/}
                  <Pagination onPrevious={() => setPage(page - 1)} onNext={() => setPage(page + 1)} currentPage={page} totalPages={totalPages} />
                  {/*Pagination*/}
                </div>
              </div>
            ) : (
              <h1>{t('subscriptions.noData')}</h1>
            )
          ) : (
            <h1>{t('subscriptions.noPermission')}</h1>
          )}
        </div>
      </main>
    </>
  );
};

export default SubscriptionsPage;
